/**
 * Generic ASCII DXF document writer (AutoCAD 2000, AC1015 — the first version
 * that can persist drawing units via $INSUNITS).
 *
 * Callers hand it finished 2D entities — polylines and text, in feet, y-up —
 * and it wraps them in Autodesk's documented minimal-file skeleton: HEADER
 * ($ACADVER/$HANDSEED/$INSUNITS/$EXTMIN/$EXTMAX), empty CLASSES, the nine
 * symbol tables, *Model_Space/*Paper_Space block records + blocks, entities
 * owned by model space, and the root dictionary — because strict importers
 * reject files missing any of it as corrupt. Group codes are 3-column
 * right-aligned with CRLF line endings, matching canonical files.
 *
 * Only layers that carry entities are declared (plus the mandatory layer 0) —
 * empty declared layers show up as "unsupported" noise in import dialogs.
 */

import { Point } from '@kaiizen/kai-floor-plan';

export interface DxfPolyline {
  kind: 'polyline';
  layer: string;
  points: Point[];
  closed: boolean;
  /** Constant polyline width (feet) — a stroked band rather than a thin line. */
  width?: number;
  dashed?: boolean;
}

export interface DxfText {
  kind: 'text';
  layer: string;
  text: string;
  /** Center of the text (middle-center aligned). */
  position: Point;
  height: number;
}

export type DxfEntity = DxfPolyline | DxfText;

const INSUNITS_FEET = 2;
const MODEL_SPACE_HANDLE = '1F';
const FIRST_ENTITY_HANDLE = 0x100;

/** DXF strings escape nothing, but control characters would corrupt the pairs. */
const dxfText = (text: string): string => text.replace(/[\r\n]+/g, ' ');

export const dxfDocument = (entities: DxfEntity[], layerColors: Record<string, number> = {}): string => {
  const rows: (number | string)[] = [];
  const put = (...pairs: (number | string)[]) => rows.push(...pairs);

  // --- HEADER: version, handle seed, units, drawing extents --------------------
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const extend = (p: Point) => {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  };
  for (const e of entities) e.kind === 'polyline' ? e.points.forEach(extend) : extend(e.position);
  put(0, 'SECTION', 2, 'HEADER');
  put(9, '$ACADVER', 1, 'AC1015');
  put(9, '$HANDSEED', 5, 'FFFFF');
  put(9, '$INSUNITS', 70, INSUNITS_FEET);
  if (entities.length) {
    put(9, '$EXTMIN', 10, minX, 20, minY, 30, 0);
    put(9, '$EXTMAX', 10, maxX, 20, maxY, 30, 0);
  }
  put(0, 'ENDSEC');

  put(0, 'SECTION', 2, 'CLASSES', 0, 'ENDSEC');

  // --- TABLES: the nine symbol tables strict importers require -----------------
  put(0, 'SECTION', 2, 'TABLES');
  const table = (name: string, handle: string, recordCount: number, records?: () => void) => {
    put(0, 'TABLE', 2, name, 5, handle, 330, '0', 100, 'AcDbSymbolTable', 70, recordCount);
    if (name === 'DIMSTYLE') put(100, 'AcDbDimStyleTable', 71, recordCount);
    records?.();
    put(0, 'ENDTAB');
  };
  const record = (type: string, handle: string, owner: string, subclass: string, ...body: (number | string)[]) =>
    put(0, type, 5, handle, 330, owner, 100, 'AcDbSymbolTableRecord', 100, subclass, ...body);
  table('VPORT', '8', 0);
  table('LTYPE', '5', 4, () => {
    record('LTYPE', '14', '5', 'AcDbLinetypeTableRecord', 2, 'ByBlock', 70, 0, 3, '', 72, 65, 73, 0, 40, 0);
    record('LTYPE', '15', '5', 'AcDbLinetypeTableRecord', 2, 'ByLayer', 70, 0, 3, '', 72, 65, 73, 0, 40, 0);
    record('LTYPE', '16', '5', 'AcDbLinetypeTableRecord', 2, 'Continuous', 70, 0, 3, 'Solid line', 72, 65, 73, 0, 40, 0);
    record('LTYPE', '18', '5', 'AcDbLinetypeTableRecord', 2, 'DASHED', 70, 0, 3, 'Dashed', 72, 65, 73, 2, 40, 0.75, 49, 0.5, 74, 0, 49, -0.25, 74, 0);
  });
  const usedLayers = [...new Set(entities.map(e => e.layer))].sort();
  table('LAYER', '2', 1 + usedLayers.length, () => {
    record('LAYER', '30', '2', 'AcDbLayerTableRecord', 2, '0', 70, 0, 62, 7, 6, 'Continuous');
    usedLayers.forEach((layer, i) =>
      record('LAYER', (0x31 + i).toString(16).toUpperCase(), '2', 'AcDbLayerTableRecord', 2, layer, 70, 0, 62, layerColors[layer] ?? 7, 6, 'Continuous')
    );
  });
  table('STYLE', '3', 0);
  table('VIEW', '6', 0);
  table('UCS', '7', 0);
  table('APPID', '9', 1, () => record('APPID', '17', '9', 'AcDbRegAppTableRecord', 2, 'ACAD', 70, 0));
  table('DIMSTYLE', 'A', 0);
  table('BLOCK_RECORD', '1', 2, () => {
    record('BLOCK_RECORD', MODEL_SPACE_HANDLE, '1', 'AcDbBlockTableRecord', 2, '*Model_Space');
    record('BLOCK_RECORD', '1B', '1', 'AcDbBlockTableRecord', 2, '*Paper_Space');
  });
  put(0, 'ENDSEC');

  // --- BLOCKS: the two mandatory space blocks, empty ---------------------------
  put(0, 'SECTION', 2, 'BLOCKS');
  const spaceBlock = (name: string, ownerHandle: string, blockHandle: string, endHandle: string) => {
    put(0, 'BLOCK', 5, blockHandle, 330, ownerHandle, 100, 'AcDbEntity', 8, '0', 100, 'AcDbBlockBegin');
    put(2, name, 70, 0, 10, 0, 20, 0, 30, 0, 3, name, 1, '');
    put(0, 'ENDBLK', 5, endHandle, 330, ownerHandle, 100, 'AcDbEntity', 8, '0', 100, 'AcDbBlockEnd');
  };
  spaceBlock('*Model_Space', MODEL_SPACE_HANDLE, '20', '21');
  spaceBlock('*Paper_Space', '1B', '22', '23');
  put(0, 'ENDSEC');

  // --- ENTITIES -----------------------------------------------------------------
  put(0, 'SECTION', 2, 'ENTITIES');
  let nextHandle = FIRST_ENTITY_HANDLE;
  const entityHead = (type: string, layer: string, dashed?: boolean) => {
    put(0, type, 5, (nextHandle++).toString(16).toUpperCase(), 330, MODEL_SPACE_HANDLE);
    put(100, 'AcDbEntity', 8, layer);
    if (dashed) put(6, 'DASHED');
  };
  for (const e of entities) {
    if (e.kind === 'polyline') {
      entityHead('LWPOLYLINE', e.layer, e.dashed);
      put(100, 'AcDbPolyline', 90, e.points.length, 70, e.closed ? 1 : 0, 43, e.width ?? 0);
      for (const p of e.points) put(10, p.x, 20, p.y);
    } else {
      entityHead('TEXT', e.layer);
      put(100, 'AcDbText', 10, e.position.x, 20, e.position.y, 30, 0, 40, e.height, 1, dxfText(e.text), 72, 1);
      put(11, e.position.x, 21, e.position.y, 31, 0, 100, 'AcDbText', 73, 2);
    }
  }
  put(0, 'ENDSEC');

  // --- OBJECTS: the root dictionary strict importers expect --------------------
  put(0, 'SECTION', 2, 'OBJECTS');
  put(0, 'DICTIONARY', 5, 'C', 330, '0', 100, 'AcDbDictionary', 281, 1, 3, 'ACAD_GROUP', 350, 'D');
  put(0, 'DICTIONARY', 5, 'D', 330, 'C', 100, 'AcDbDictionary', 281, 1);
  put(0, 'ENDSEC', 0, 'EOF');

  // Pairs → lines: group codes right-aligned to 3 columns, CRLF endings.
  return rows.map((row, i) => (i % 2 === 0 ? String(row).padStart(3, ' ') : String(row))).join('\r\n') + '\r\n';
};
